import Script from 'next/script'
import {
    ApiShopperLandingPage,
    DESKTOP_PARAM,
    IS_EDITOR_PREVIEW_PARAM,
    IS_PREVIEW_PARAM,
    NO_PIXELS_PARAM,
    PREVIEW_HOSTNAME,
} from '@storefronts/common-types'
import { Constants } from '../../style/styleConstants'

/**
 * Body of the `beforeInteractive` redirect script, exported so its bail-outs can
 * be evaluated under test — it has to ship as a string, so it can't reuse any
 * helper from this module at runtime.
 */
export const desktopRedirectScriptBody = (redirectUrl: string): string => {
    // JSON.stringify yields a valid JS string literal, but the HTML parser
    // terminates the surrounding <script> on any literal "</script>" inside
    // it. Escape "<" (and the U+2028/U+2029 line separators that break JS
    // string literals) so an admin-supplied URL can't break out of the tag.
    const urlLiteral = JSON.stringify(redirectUrl)
        .replace(/</g, '\\u003c')
        .replace(/\u2028/g, '\\u2028')
        .replace(/\u2029/g, '\\u2029')

    // Mirrors IS_PREVIEW() in QueryParamContextProvider: an explicit
    // is-preview param wins; otherwise PREVIEW_HOSTNAME makes it a preview.
    return `
        (function () {
            try {
                if (window.top !== window.self) return;
                var qs = new URLSearchParams(window.location.search);
                if (qs.get('${NO_PIXELS_PARAM}') === 'true') return;
                if (qs.get('${IS_EDITOR_PREVIEW_PARAM}') === 'true') return;
                if (qs.get('${DESKTOP_PARAM}') === 'true') return;
                var isPreviewParam = qs.get('${IS_PREVIEW_PARAM}');
                var isPreview = isPreviewParam !== null
                    ? isPreviewParam === 'true'
                    : window.location.hostname.indexOf('${PREVIEW_HOSTNAME}') !== -1;
                if (isPreview) return;
                if (window.innerWidth < ${Constants.EXPANDED_BREAKPOINT}) return;
                var target = new URL(${urlLiteral});
                qs.forEach(function (value, key) {
                    if (!target.searchParams.has(key)) {
                        target.searchParams.set(key, value);
                    }
                });
                window.location.replace(target.toString());
            } catch (e) {
                // swallow - never block page render
            }
        })();
    `
}

interface DesktopRedirectScriptProps {
    landingPage: ApiShopperLandingPage
}

const DesktopRedirectScript = ({ landingPage }: DesktopRedirectScriptProps) => {
    // Per-storefront override falls back to the account-wide setting.
    const clientDesktopRedirectEnabled =
        landingPage.settings?.desktopRedirectEnabled ?? false
    const clientDesktopRedirectUrl =
        landingPage.settings?.desktopRedirectUrl ?? null

    const landingPageDesktopRedirectEnabled =
        landingPage.desktopRedirectEnabledOverride ??
        clientDesktopRedirectEnabled
    const landingPageDesktopRedirectUrl =
        landingPage.desktopRedirectUrlOverride ?? clientDesktopRedirectUrl

    if (!landingPageDesktopRedirectEnabled || !landingPageDesktopRedirectUrl)
        return null

    return (
        <Script id="desktop-redirect" strategy="beforeInteractive">
            {desktopRedirectScriptBody(landingPageDesktopRedirectUrl)}
        </Script>
    )
}

DesktopRedirectScript.displayName = 'DesktopRedirectScript'

export { DesktopRedirectScript }
